/**
 * Решение о перемещении задачи. Чистая функция — §28.2 требует, чтобы
 * бизнес-правила были чистыми и покрытыми тестами.
 *
 * Вынесено из обработчика перетаскивания намеренно: внутри DndContext это
 * правило недостижимо для теста, а оно — главное в канбане. Здесь же
 * проверяется §12.3 (разрешённые переходы) и §12.2 (требование исполнителя
 * при входе в столбец).
 *
 * §12.1: перемещение несёт и место в столбце. Прежде бросок в тот же
 * столбец считался «не действием», а в другой столбец карточка всегда
 * вставала в конец — поменять порядок карточек было нельзя.
 *
 * Клиентская проверка не заменяет серверную: сервер отклонит переход
 * независимо от того, что решил клиент (§1.4). Смысл в том, чтобы
 * пользователь узнал об отказе до броска, а не после.
 */

import type { KanbanCardData, KanbanColumnData, MoveIntent } from "./kanbanTypes";

export type MoveDecision =
  | { allowed: true; intent: MoveIntent }
  | { allowed: false; reason: string }
  | { allowed: false; reason: null };

interface EvaluateArgs {
  card: KanbanCardData;
  sourceColumn: KanbanColumnData;
  targetColumnId: string;
  columns: KanbanColumnData[];
  /** Карточка под курсором при броске; не задана — пустое место столбца, в конец. */
  overCardKey?: string | null;
  /** Бросок ниже середины карточки под курсором — встать после неё. */
  placeAfter?: boolean;
}

export function evaluateMove({
  card,
  sourceColumn,
  targetColumnId,
  columns,
  overCardKey = null,
  placeAfter = false,
}: EvaluateArgs): MoveDecision {
  const target = columns.find((column) => column.id === targetColumnId);
  if (!target) return { allowed: false, reason: null };

  // Бросок на саму себя — карточку не сдвинули.
  if (overCardKey === card.key) return { allowed: false, reason: null };

  const sameColumn = targetColumnId === sourceColumn.id;

  if (!sameColumn) {
    // §12.3: доска без объявленных переходов разрешает любые — иначе
    // только что созданная доска оказалась бы неработоспособной до ручной
    // настройки графа. Сервер считает именно так; трактовать пустой список
    // как запрет значило бы завести второе, расходящееся правило, и канбан
    // молча переставал бы работать на каждой новой доске.
    const graphDeclared = sourceColumn.allowedTargets.length > 0;

    if (graphDeclared && !sourceColumn.allowedTargets.includes(targetColumnId)) {
      return {
        allowed: false,
        reason: `Переход из «${sourceColumn.name}» в «${target.name}» не разрешён на этой доске`,
      };
    }

    if (target.requiresAssignee && card.assignees.length === 0) {
      return {
        allowed: false,
        reason: `Столбец «${target.name}» требует назначенного исполнителя`,
      };
    }
  }

  // Место — среди карточек целевого столбца без самой перетаскиваемой.
  const others = target.cards.filter((item) => item.key !== card.key);
  const overIndex = overCardKey === null ? -1 : others.findIndex((item) => item.key === overCardKey);
  const position = overIndex === -1 ? others.length : overIndex + (placeAfter ? 1 : 0);

  // Бросок на прежнее место в своём столбце — не перемещение и не ошибка.
  if (sameColumn && position === sourceColumn.cards.findIndex((item) => item.key === card.key)) {
    return { allowed: false, reason: null };
  }

  return {
    allowed: true,
    intent: {
      taskKey: card.key,
      fromColumnId: sourceColumn.id,
      toColumnId: targetColumnId,
      position,
      afterTaskKey: position === 0 ? null : (others[position - 1]?.key ?? null),
    },
  };
}

/** Бросок приходится либо на колонку, либо на карточку внутри неё. */
export function resolveColumnId(overId: string, columns: KanbanColumnData[]): string | null {
  if (columns.some((column) => column.id === overId)) return overId;
  for (const column of columns) {
    if (column.cards.some((card) => card.key === overId)) return column.id;
  }
  return null;
}
